import React, { useState, useEffect, startTransition } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppContext } from '../../contexts/AppContext';
import userDataService from '../../services/UserDataService';
import { isOwnerEmail } from '../../services/CommunicationService';

interface SidebarProps {
  
}

const Sidebar: React.FC<SidebarProps> = () => {
  const { state } = useAppContext();
  const { currentUser } = state;
  const location = useLocation();
  const navigate = useNavigate();
  const [lowStockCount, setLowStockCount] = useState(0);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  useEffect(() => {
    
    const checkLowStock = async () => {
      if (!currentUser) return;
      
      try {
        const lowStockParts = await userDataService.getLowStockParts(currentUser.id);
        setLowStockCount(lowStockParts.length);
      } catch (error) {
        console.error('Error checking low stock:', error);
      }
    };

    checkLowStock();
    
    const interval = setInterval(checkLowStock, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [currentUser]);
  
  const isEspAdmin = isOwnerEmail(currentUser?.email);

  const navItems = [
    { path: '/dashboard', label: 'Dashboard', icon: 'dashboard', group: 'Command' },
    { path: '/notifications', label: 'Notifications', icon: 'bell', group: 'Command' },
    { path: '/trucks', label: 'Fleet Management', icon: 'truck', group: 'Command' },
    { path: '/maintenance', label: 'Maintenance', icon: 'wrench', group: 'Operations' },
    { path: '/field-work', label: 'Field Work', icon: 'mobile', group: 'Operations' },
    { path: '/work-orders', label: 'Work Orders', icon: 'clipboard', group: 'Operations' },
    ...(isEspAdmin ? [{ path: '/monitoring-data', label: 'Monitoring Data', icon: 'dashboard-chart', group: 'Operations' }] : []),
    { path: '/parts', label: 'Parts & Inventory', icon: 'cog', badge: lowStockCount > 0 ? lowStockCount : undefined, group: 'Operations' },
    { path: '/suppliers', label: 'Suppliers', icon: 'supplier', group: 'Operations' },
    { path: '/integrations', label: 'Integrations', icon: 'signal', group: 'Enterprise' },
    { path: '/compliance', label: 'Compliance', icon: 'shield', group: 'Enterprise' },
    { path: '/predictive-maintenance', label: 'Predictive', icon: 'brain', group: 'Intelligence' },
    { path: '/reports', label: 'Reports', icon: 'chart', group: 'Intelligence' },
    { path: '/analytics', label: 'Analytics', icon: 'chart', group: 'Intelligence' },
    { path: '/calendar', label: 'Schedule Calendar', icon: 'calendar', group: 'Enterprise' },
    { path: '/company', label: 'Company', icon: 'user', group: 'Enterprise' },
  ];

  
  if (currentUser?.role === 'admin' || isOwnerEmail(currentUser?.email)) {
    navItems.push(
      { path: '/admin', label: 'Admin Panel', icon: 'admin', group: 'Admin' },
      { path: '/bulk-manager', label: 'Bulk Operations', icon: 'upload', group: 'Admin' }
    );
  }

  const activeItem = navItems.find(item =>
    location.pathname === item.path || (item.path === '/dashboard' && location.pathname === '/')
  ) || navItems[0];

  const handleNavigate = (path: string) => {
    setMobileNavOpen(false);
    startTransition(() => navigate(path));
  };

  const renderIcon = (iconName: string) => {
    const iconProps = { width: "20", height: "20", viewBox: "0 0 24 24", fill: "currentColor" };
    
    switch (iconName) {
      case 'dashboard':
        return (
          <svg {...iconProps}>
            <path d="M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z"/>
          </svg>
        );
      case 'truck':
        return (
          <svg {...iconProps}>
            <path d="M20 8h-3V4H3c-1.1 0-2 .9-2 2v11h2c0 1.66 1.34 3 3 3s3-1.34 3-3h6c0 1.66 1.34 3 3 3s3-1.34 3-3h2v-5l-3-4zM6 18.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm13.5-9l1.96 2.5H17V9.5h2.5zm-1.5 9c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/>
          </svg>
        );
      case 'wrench':
        return (
          <svg {...iconProps}>
            <path d="M22.7 19l-9.1-9.1c.9-2.3.4-5-1.5-6.9-2-2-5-2.4-7.4-1.3L9 6 6 9 1.6 4.7C.4 7.1.9 10.1 2.9 12.1c1.9 1.9 4.6 2.4 6.9 1.5l9.1 9.1c.4.4 1 .4 1.4 0l2.3-2.3c.5-.4.5-1.1.1-1.4z"/>
          </svg>
        );
      case 'mobile':
        return (
          <svg {...iconProps}>
            <path d="M17 1H7c-1.1 0-2 .9-2 2v18c0 1.1.9 2 2 2h10c1.1 0 2-.9 2-2V3c0-1.1-.9-2-2-2zm-5 21c-.83 0-1.5-.67-1.5-1.5S11.17 19 12 19s1.5.67 1.5 1.5S12.83 22 12 22zm5-4H7V4h10v14z"/>
          </svg>
        );
      case 'clipboard':
        return (
          <svg {...iconProps}>
            <path d="M19 3h-4.18C14.4 1.84 13.3 1 12 1S9.6 1.84 9.18 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 0c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm2 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>
          </svg>
        );
      case 'signal':
        return (
          <svg {...iconProps}>
            <path d="M2 22h20V2L2 22zm18-5h-3v-3h3v3zm0-5h-3V9h3v3zm0-5h-3V4h3v3z"/>
          </svg>
        );
      case 'shield':
        return (
          <svg {...iconProps}>
            <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-1 15l-4-4 1.41-1.41L11 13.17l5.59-5.59L18 9l-7 7z"/>
          </svg>
        );
      case 'brain':
        return (
          <svg {...iconProps}>
            <path d="M9 2C6.79 2 5 3.79 5 6v.17C3.83 6.58 3 7.69 3 9c0 .96.45 1.81 1.14 2.36C3.43 12.08 3 13.05 3 14.12 3 16.26 4.74 18 6.88 18H8v-2H6.88C5.84 16 5 15.16 5 14.12c0-.86.58-1.61 1.42-1.82l1.22-.31-.93-.85C6.26 10.73 6 10.15 6 9.5 6 8.67 6.67 8 7.5 8H9V2zm6 0v6h1.5c.83 0 1.5.67 1.5 1.5 0 .65-.26 1.23-.71 1.64l-.93.85 1.22.31c.84.21 1.42.96 1.42 1.82 0 1.04-.84 1.88-1.88 1.88H16v2h1.12C19.26 18 21 16.26 21 14.12c0-1.07-.43-2.04-1.14-2.76C20.55 10.81 21 9.96 21 9c0-1.31-.83-2.42-2-2.83V6c0-2.21-1.79-4-4-4zM10 4h4v16h-4V4z"/>
          </svg>
        );
      case 'cog':
        return (
          <svg {...iconProps}>
            <path d="M19.14,12.94c0.04-0.3,0.06-0.61,0.06-0.94c0-0.32-0.02-0.64-0.07-0.94l2.03-1.58c0.18-0.14,0.23-0.41,0.12-0.61 l-1.92-3.32c-0.12-0.22-0.37-0.29-0.59-0.22l-2.39,0.96c-0.5-0.38-1.03-0.7-1.62-0.94L14.4,2.81c-0.04-0.24-0.24-0.41-0.48-0.41 h-3.84c-0.24,0-0.43,0.17-0.47,0.41L9.25,5.35C8.66,5.59,8.12,5.92,7.63,6.29L5.24,5.33c-0.22-0.08-0.47,0-0.59,0.22L2.74,8.87 C2.62,9.08,2.66,9.34,2.86,9.48l2.03,1.58C4.84,11.36,4.8,11.69,4.8,12s0.02,0.64,0.07,0.94l-2.03,1.58 c-0.18,0.14-0.23,0.41-0.12,0.61l1.92,3.32c0.12,0.22,0.37,0.29,0.59,0.22l2.39-0.96c0.5,0.38,1.03,0.7,1.62,0.94l0.36,2.54 c0.05,0.24,0.24,0.41,0.48,0.41h3.84c0.24,0,0.44-0.17,0.47-0.41l0.36-2.54c0.59-0.24,1.13-0.56,1.62-0.94l2.39,0.96 c0.22,0.08,0.47,0,0.59-0.22l1.92-3.32c0.12-0.22,0.07-0.47-0.12-0.61L19.14,12.94z M12,15.6c-1.98,0-3.6-1.62-3.6-3.6 s1.62-3.6,3.6-3.6s3.6,1.62,3.6,3.6S13.98,15.6,12,15.6z"/>
          </svg>
        );
      case 'chart':
        return (
          <svg {...iconProps}>
            <path d="M3.5 18.49l6-6.01 4 4L22 6.92l-1.41-1.41-7.09 7.97-4-4L2 16.99z"/>
          </svg>
        );
      case 'calendar':
        return (
          <svg {...iconProps}>
            <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z"/>
          </svg>
        );
      case 'supplier':
        return (
          <svg {...iconProps}>
            <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
          </svg>
        );
      case 'dashboard-chart':
        return (
          <svg {...iconProps}>
            <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z"/>
          </svg>
        );
      case 'upload':
        return (
          <svg {...iconProps}>
            <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"/>
          </svg>
        );
      case 'bell':
        return (
          <svg {...iconProps}>
            <path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.89 2 2 2zm6-6v-5c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z"/>
          </svg>
        );
      case 'user':
        return (
          <svg {...iconProps}>
            <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
          </svg>
        );
      case 'admin':
        return (
          <svg {...iconProps}>
            <path d="M17,11C17,10.34 16.67,9.74 16.12,9.4L13,7.59V6A1,1 0 0,0 12,5A1,1 0 0,0 11,6V7.59L7.88,9.4C7.33,9.74 7,10.34 7,11V16A1,1 0 0,0 8,17H16A1,1 0 0,0 17,16V11M12,3A3,3 0 0,1 15,6H13.5A1.5,1.5 0 0,0 12,4.5A1.5,1.5 0 0,0 10.5,6H9A3,3 0 0,1 12,3Z"/>
          </svg>
        );
      case 'repeat':
        return (
          <svg {...iconProps}>
            <path d="M17 17H7V14L3 18l4 4v-3h12v-2zM7 7h10v3l4-4-4-4v3H5v2h2z"/>
          </svg>
        );
      case 'warning':
        return (
          <svg {...iconProps}>
            <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>
          </svg>
        );
      default:
        return null;
    }
  };

  return (
    <div className="sidebar sidebar-open">
      <button
        type="button"
        className="mobile-nav-summary"
        aria-expanded={mobileNavOpen}
        onClick={() => setMobileNavOpen(open => !open)}
      >
        <span className="mobile-nav-current-icon">{renderIcon(activeItem.icon)}</span>
        <span className="mobile-nav-copy">
          <span className="mobile-nav-eyebrow">Current area</span>
          <strong>{activeItem.label}</strong>
        </span>
        <span className="mobile-nav-toggle">{mobileNavOpen ? 'Close' : 'Menu'}</span>
      </button>

      <nav className={`sidebar-nav ${mobileNavOpen ? 'mobile-nav-open' : ''}`}>
        {navItems.map((item, index) => {
          const isActive = location.pathname === item.path || 
            (item.path === '/dashboard' && location.pathname === '/');
          const previousItem = navItems[index - 1];
          const shouldShowGroup = !previousItem || previousItem.group !== item.group;
          
          return (
            <React.Fragment key={item.path}>
              {shouldShowGroup && <div className="nav-group-label">{item.group}</div>}
              <button
                onClick={() => handleNavigate(item.path)}
                className={`nav-item ${isActive ? 'active' : ''}`}
              >
                <span className="nav-icon">{renderIcon(item.icon)}</span>
                <span className="nav-label">{item.label}</span>
                {item.badge && (
                  <span className="nav-badge">
                    {item.badge}
                  </span>
                )}
              </button>
            </React.Fragment>
          );
        })}
      </nav>
    </div>
  );
};

export default Sidebar;
